import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {
  adjustToTimeZone,
  adjustToTimeZoneSafely,
  EntityIdentifier,
  firstOrUndefined,
  getObjectId,
  PipelineFlowEntity,
  PipelineFlowSearchCriteria,
  pipelineFlowsSelector,
  startOfDay,
  useSearchPipelineFlowsQuery,
} from "@ots-frontend/ots-data";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {PipelineFlowDataTable} from "../../components/data-table/PipelineFlowDataTable/PipelineFlowDataTable";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {selectUIPipelineFlowSearchFilter} from "../../store/entity/pipeline-flow/selectors";
import {useOtsUISelector} from "../../hooks/useOtsUISelector";
import {useDispatch} from "react-redux";
import {setUIPipelineFlowSearchFilter} from "../../store/entity/pipeline-flow/actions";
import {useNavigate} from "react-router-dom";
import {usePipelineFlowMutation} from "../../hooks/usePipelineFlowMutation";
import {HasAccess} from "../../components/auth/HasAccess/HasAccess";
import {PipelineFlowSearchToolbar} from "../../components/toolbar/PipelineFlowSearchToolbar/PipelineFlowSearchToolbar";
import {isEqual} from "lodash";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {PipelineFlowSearchFormValues} from "../../components/form/PipelineFlowSearchForm/PipelineFlowSearchForm";
import {ExportLink} from "../../components/exportable/ExportLink/ExportLink";

export type PipelineFlowSearchPageProps = {}

export const PipelineFlowSearchPage = (props: PipelineFlowSearchPageProps) => {

  const {regionId, appCategory} = useApp();
  const {t} = useTranslation(["common"]);
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const criteria = useOtsUISelector(selectUIPipelineFlowSearchFilter)

  const selectPipelineFlows = useMemo(() => {
    return pipelineFlowsSelector();
  }, []);

  const searchArg: PipelineFlowSearchCriteria | typeof skipToken = useMemo(() => {
    if (regionId && criteria?.fromDttm) {
      return {
        ...criteria,
        fromDttm: adjustToTimeZone(criteria.fromDttm).getTime(),
        toDttm: adjustToTimeZoneSafely(criteria.toDttm)?.getTime(),
        regionIds: [regionId],
        categories: appCategory ? [appCategory.code] : undefined,
      }
    } else {
      return skipToken
    }
  }, [criteria, regionId, appCategory])

  const {pipelineFlows, isFetching, refetch} = useSearchPipelineFlowsQuery(searchArg, {
    selectFromResult: (result) => ({
      ...result,
      pipelineFlows: selectPipelineFlows(result, {regionId: regionId}),
      isFetching: result.isFetching,
    })
  });

  const onFilterChange = useCallback((values: PipelineFlowSearchFormValues) => {
    let entityIds: EntityIdentifier[] | undefined = undefined
    if (values.entityIds && values.entityType) {
      entityIds = []
      for (const id of values.entityIds) {
        entityIds.push({
          id: id,
          type: values.entityType,
        })
      }
    }

    const freightTypeId = getObjectId(values.freightType)

    const newCriteria: PipelineFlowSearchCriteria = {
      fromDttm: values.fromDate.getTime(),
      toDttm: values.toDate?.getTime(),
      entityIds: entityIds,
      freightTypeIds: freightTypeId ? [freightTypeId] : undefined,
      meterGenerated: values.meterGenerated,
      ignoreMeterGenerated: values.ignoreMeterGenerated ?? true,
    }

    let criteriaChange = false
    if (criteria?.fromDttm !== newCriteria.fromDttm) {
      criteriaChange = true
    }
    if (criteria?.toDttm !== newCriteria.toDttm) {
      criteriaChange = true
    }
    if (!isEqual(criteria?.entityIds, newCriteria.entityIds)) {
      criteriaChange = true
    }
    if (!isEqual(criteria?.freightTypeIds, newCriteria.freightTypeIds)) {
      criteriaChange = true
    }
    if (criteria?.meterGenerated !== newCriteria.meterGenerated) {
      criteriaChange = true
    }
    if (criteria?.ignoreMeterGenerated !== newCriteria.ignoreMeterGenerated) {
      criteriaChange = true
    }

    if (!criteriaChange) {
      if (refetch) {
        refetch()
      }
    } else {
      dispatch(setUIPipelineFlowSearchFilter({
        ...newCriteria,
      }))
    }
  }, [criteria, refetch, dispatch]);

  const {
    deletePipelineFlow,
  } = usePipelineFlowMutation()

  const onNewClick = useCallback(() => {
    navigate("form")
  }, [navigate])

  const onEditClick = useCallback((record: PipelineFlowEntity) => {
    navigate("form", {
      state: {
        id: record.id,
      }
    })
  }, [navigate])

  const onDeleteClick = useCallback((record: PipelineFlowEntity) => {
    deletePipelineFlow(record.id, undefined)
  }, [deletePipelineFlow])

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs p="sm"/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("page.entity.pipelineflow.search.title", "Search Flow Volumes")}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t('common.action.plural', "Actions")}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("common.action.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <ExportLink data={pipelineFlows}>
                          <Menu.Item>
                            {t("common.action.export", "Export", {ns: "common"})}
                          </Menu.Item>
                        </ExportLink>
                        <HasAccess permissions={["edit"]}>
                          <>
                            <Menu.Divider/>
                            <Menu.Item onClick={onNewClick}>
                              {t("common.action.new", "New", {ns: "common"})}
                            </Menu.Item>
                          </>
                        </HasAccess>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <PipelineFlowSearchToolbar
                    defaultValues={{
                      fromDate: criteria?.fromDttm ? new Date(criteria?.fromDttm) : startOfDay(new Date()),
                      toDate: criteria?.toDttm ? new Date(criteria?.toDttm) : undefined,
                      entityType: firstOrUndefined(criteria?.entityIds)?.type,
                      entityIds: criteria?.entityIds?.map(eid => eid.id),
                      freightType: firstOrUndefined(criteria?.freightTypeIds),
                      meterGenerated: criteria?.meterGenerated,
                      ignoreMeterGenerated: criteria?.ignoreMeterGenerated ?? true,
                    }}
                    options={{
                      regionId: regionId,
                      category: appCategory,
                    }}
                    submitting={isFetching}
                    onSubmit={onFilterChange}/>
                </Card>
                <PipelineFlowDataTable
                  withBorder
                  records={pipelineFlows}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isFetching}
                  onEdit={onEditClick}
                  onDelete={onDeleteClick}/>
              </Stack>

            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
